import typia from 'typia';
import { dispatchAction, type Action } from '@ardoq/rxbeach';
import { WHEN_TO_USE, CHANGE_VIEW_SETTINGS_CONTEXT_DESCRIPTION, } from './descriptions';
import { BUBBLE_AI_CONTEXT_SETTING_KEYS, COMPONENT_MATRIX_2_AI_CONTEXT_SETTING_KEYS, DEPENDENCY_MAP_AI_CONTEXT_SETTING_KEYS, FLEXIBLE_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS, MODERNIZED_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS, TIMELINE_AI_CONTEXT_SETTING_KEYS, toLayoutOrientation, toLayoutOrientationAiValue, RELATIONSHIPS_AI_CONTEXT_SETTING_KEYS, type LayoutOrientationAiValue, BLOCKS_AI_CONTEXT_SETTING_KEYS, } from 'appLayout/ardoqStudio/viewpointViewSettings';
import { updateViewSettings } from '@ardoq/view-settings';
import { logError } from '@ardoq/logging';
import { ViewIds } from '@ardoq/api-types';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { toRenderedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import type { AI_VIEW_IDS } from 'aiControlledCommands/viewpoint/aiViewIds';
import { SettableModernizedBlockDiagramViewSettings } from 'tabview/blockDiagram/types';
import type { ComponentMatrix2AiViewProperties, ComponentMatrix2SettingsBounds, SettableComponentMatrix2ViewSettings, } from 'tabview/componentMatrix/componentMatrix2/types';
import { toAiComponentMatrix2ViewSettings, toStoredComponentMatrix2ViewSettings, } from 'tabview/componentMatrix/componentMatrix2/aiViewSettings';
import { COMPONENT_MATRIX_2_AI_VIEW_STATE_KEYS } from 'tabview/componentMatrix/componentMatrix2/settingsDependencies';
import { BUBBLE_CHART_AI_VIEW_STATE_KEYS, type SettableBubbleChartViewSettings, } from '@ardoq/bubble-chart';
import { toStoredBubbleChartViewSettings } from 'tabview/bubbleChart/aiViewSettings';
import { isTimelineSettingsControlsDependencies, TIMELINE_AI_VIEW_STATE_KEYS, type AiTimelineViewSettings, type TimelineSettingsBounds, type TimelineSettingsControlsDependencies, } from '@ardoq/timeline2024';
import { toAiTimelineViewSettings, toStoredTimelineViewSettings, } from 'tabview/timeline2024/aiViewSettings';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import type { AiControlledCommand, CommandExecutionState, } from 'aiControlledCommands/types';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { VIEWPOINT_DOMAIN } from '../domain';
import { isEmpty, isEqual, pick } from 'lodash';
import { staticRejection, toCommandRejection, type ValidationRejection, } from 'aiControlledCommands/validationRejection';
import type { ViewpointModeViewSettingsShapeWithoutSymbols, ViewpointModeViewSettingsWithChanges, } from 'views/environmentals/viewSettings/types';
import { SettableDependencyMapViewSettings } from '@ardoq/dependency-map';
import { CurrentViewPropertiesState, readAiBubbleChartViewProperties, readAiComponentMatrix2ViewProperties, hasGroupsAiProperties, readDependencyMapSettingsControlsDependencies, } from './currentViewProperties$';
import type { ViewpointModeDependencyMapSettingsControlsDependencies } from 'tabview/dependencyMap/viewpointMode/settingsControls';
import type { ViewpointModeBubbleChartSettingsControlsDependencies } from 'tabview/bubbleChart/viewpointMode/settingsControls';
import { isAllCollapsed, isRecord, WILDCARD } from '@ardoq/common-helpers';
import { relationshipDiagramCollapseAllGroups, relationshipDiagramExpandAllGroups, relationshipDiagramSetCollapsedGroupIds, } from 'tabview/relationshipDiagrams/actions';
import { SettableRelationshipsViewSettings } from 'tabview/relationsD3View/hierarchical/types';
import { relationshipsViewSetCollapsedGroupIds } from 'tabview/relationsD3View/hierarchical/actions';
import { hasViewInstanceId } from '@ardoq/graph';
import { FlexibleBlockDiagramViewSettings } from 'tabview/flexibleBlockDiagram/types';
import type { BlocksLayoutType } from 'tabview/visualizations/aq-blocks/types';
import { flexibleBlockDiagramCollapseAll, flexibleBlockDiagramExpandAll, flexibleBlockDiagramSetCollapsedGroupIds, } from 'tabview/flexibleBlockDiagram/actions';
import { SettableBlocksViewSettings } from 'tabview/blocks/types';
type AiDependencyMapViewSettings = Partial<Omit<SettableDependencyMapViewSettings, 'degreesOfParenthood' | 'treeDepth'>> & {
    /**
     * The number of reference-parent levels to display.
     *
     * Include this only when
     * viewState.dependencyMap.canSetParentDegrees is true.
     *
     * Use -1 when the user asks for all or unlimited reference-parent levels.
     * Otherwise, use a whole number from 0 through
     * viewState.dependencyMap.referenceParentMaxDepth.
     *
     * A value of 0 is valid and includes no reference-parent levels.
     */
    degreesOfParenthood?: number;
    /**
     * The number of tree levels to keep expanded, counting each root as level 1.
     *
     * Include this only when viewState.dependencyMap.canSetTreeDepth is true.
     * Use a whole number from 1 through
     * viewState.dependencyMap.maxTreeDepth.
     *
     * A value equal to maxTreeDepth means the tree is fully expanded.
     */
    treeDepth?: number;
};
type AiFlexibleBlockDiagramViewSettings = Partial<Omit<FlexibleBlockDiagramViewSettings, 'layoutOrientation' | 'hideReferences' | 'activeDiffMode' | 'layoutType'>> & {
    layoutOrientation?: LayoutOrientationAiValue;
    /**
     * Omit reference lines entirely. Takes precedence over `separateReferences`
     * when `true`, so combining or separating references has no visible effect
     * until this is `false`. When `currentSettings.hideReferences` is `true`
     * and the user asks to combine or separate references, include this key
     * set to `false` alongside `separateReferences`.
     */
    hideReferences?: boolean;
    /**
     * How blocks are arranged:
     * - `process`: a linear flow in the direction of `layoutOrientation`, best
     *   for sequential steps.
     * - `compact`: minimizes the space the diagram takes up.
     * - `seriesparallel`: groups parallel branches together, best for flows
     *   that branch and rejoin.
     * - `grid`: aligns blocks into even rows and columns.
     */
    layoutType?: Exclude<BlocksLayoutType, 'manual'>;
};
// This is a partial settings patch rather than a complete configuration.
// Keeping every property optional makes the generated JSON schema allow the AI
// to emit only settings explicitly requested by the user.
type AiViewSettingsMap = {
    [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: Partial<Omit<SettableModernizedBlockDiagramViewSettings, 'layoutOrientation'>> & {
        layoutOrientation?: LayoutOrientationAiValue;
    };
    [ViewIds.COMPONENT_MATRIX_2]: Partial<SettableComponentMatrix2ViewSettings>;
    [ViewIds.DEPENDENCY_MAP_2]: AiDependencyMapViewSettings;
    [ViewIds.BUBBLE]: Partial<SettableBubbleChartViewSettings>;
    [ViewIds.TIMELINE]: AiTimelineViewSettings;
    [ViewIds.RELATIONSHIPS_3]: Partial<SettableRelationshipsViewSettings>;
    [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: AiFlexibleBlockDiagramViewSettings;
    [ViewIds.BLOCKS]: Partial<SettableBlocksViewSettings>;
};
// typia compiles the JSDoc here into the command's JSON schema, so these
// comments are LLM-facing — keep them accurate.
export type ViewSettingsChangePayload = {
    [K in keyof AiViewSettingsMap]: {
        /** The type of the view whose settings to update. Must be the open view. */
        type: K;
        /**
         * A partial patch containing only configuration explicitly requested by
         * the user.
         *
         * Include only supported keys. Include a key only if its requested value
         * differs from `currentSettings`.
         */
        settings: AiViewSettingsMap[K];
    };
}[keyof AiViewSettingsMap];
type AiDependencyMapState = {
    /**
     * Whether the AI may modify degreesOfParenthood.
     * When false, degreesOfParenthood must not be included in settings.
     */
    canSetParentDegrees: boolean;
    /**
     * Maximum allowed value for degreesOfParenthood.
     * Meaningful only when canSetParentDegrees is true.
     */
    referenceParentMaxDepth: number;
    /**
     * Whether the AI may modify treeDepth.
     * When false, treeDepth must not be included in settings.
     */
    canSetTreeDepth: boolean;
    /**
     * Maximum finite value for treeDepth.
     * Meaningful only when canSetTreeDepth is true.
     */
    maxTreeDepth: number;
};
type AiComponentMatrix2State = Pick<ComponentMatrix2SettingsBounds, (typeof COMPONENT_MATRIX_2_AI_VIEW_STATE_KEYS)[number]>;
type AiBubbleChartState = Pick<ViewpointModeBubbleChartSettingsControlsDependencies, (typeof BUBBLE_CHART_AI_VIEW_STATE_KEYS)[number]>;
type AiTimelineState = Pick<TimelineSettingsBounds, (typeof TIMELINE_AI_VIEW_STATE_KEYS)[number]>;
/**
 * Live, non-persistent state of the currently open view.
 * This is informational context, not view settings.
 */
type AiViewRuntimeState = {
    componentMatrix2?: AiComponentMatrix2State;
    dependencyMap?: AiDependencyMapState;
    bubbleChart?: AiBubbleChartState;
    timeline?: AiTimelineState;
    /**
     * Maps each of the current view's group ids to its human-readable label.
     * Use ids from this map in settings.collapsedGroupIds when the user names
     * specific groups to collapse or expand.
     */
    groupLabels?: Record<string, string>;
};
type ChangeViewSettingsContext = {
    currentView: ViewIds | null;
    currentSettings: Record<string, unknown>;
    viewState?: AiViewRuntimeState;
};
const isChangeViewSettingsContext = (context: unknown): context is ChangeViewSettingsContext => isRecord(context) &&
    'currentView' in context &&
    isRecord(context.currentSettings);
const AI_DEPENDENCY_MAP_STATE_KEYS = [
    'canSetParentDegrees',
    'referenceParentMaxDepth',
    'canSetTreeDepth',
    'maxTreeDepth',
] as const satisfies readonly (keyof AiDependencyMapState)[];
export type AiViewDependencies = {
    viewId: ViewIds.COMPONENT_MATRIX_2;
    dependencies: ComponentMatrix2AiViewProperties;
    } | {
    viewId: ViewIds.DEPENDENCY_MAP_2;
    dependencies: ViewpointModeDependencyMapSettingsControlsDependencies;
    } | {
    viewId: ViewIds.BUBBLE;
    dependencies: ViewpointModeBubbleChartSettingsControlsDependencies;
    } | {
    viewId: ViewIds.TIMELINE;
    dependencies: TimelineSettingsControlsDependencies;
};
const readAiViewDependencies = (currentView: ViewIds | null, currentViewProperties: CurrentViewPropertiesState): AiViewDependencies | undefined => {
    if (currentView === ViewIds.COMPONENT_MATRIX_2) {
        const dependencies = readAiComponentMatrix2ViewProperties(currentViewProperties);
        return dependencies ? { viewId: currentView, dependencies } : undefined;
    }
    if (currentView === ViewIds.DEPENDENCY_MAP_2) {
        const dependencies = readDependencyMapSettingsControlsDependencies(currentViewProperties);
        return dependencies ? { viewId: currentView, dependencies } : undefined;
    }
    if (currentView === ViewIds.BUBBLE) {
        const dependencies = readAiBubbleChartViewProperties(currentViewProperties);
        return dependencies ? { viewId: currentView, dependencies } : undefined;
    }
    if (currentView === ViewIds.TIMELINE &&
        isTimelineSettingsControlsDependencies(currentViewProperties.viewProperties)) {
        return {
            viewId: currentView,
            dependencies: currentViewProperties.viewProperties,
        };
    }
    return undefined;
};
const getGroupLabelsRuntimeState = (currentView: ViewIds | null, currentViewProperties: CurrentViewPropertiesState): Record<string, string> | undefined => {
    const { viewProperties, viewPropertiesViewId } = currentViewProperties;
    if (currentView !== viewPropertiesViewId ||
        !hasGroupsAiProperties(viewProperties)) {
        return undefined;
    }
    return viewProperties.groupLabels;
};
export const getAiViewRuntimeState = (currentView: ViewIds | null, currentViewProperties: CurrentViewPropertiesState, viewDependencies: AiViewDependencies | undefined): AiViewRuntimeState | undefined => {
    const groupLabels = getGroupLabelsRuntimeState(currentView, currentViewProperties);
    if (!viewDependencies) {
        return groupLabels !== undefined ? { groupLabels } : undefined;
    }
    switch (viewDependencies.viewId) {
        case ViewIds.COMPONENT_MATRIX_2:
            return {
                componentMatrix2: pick(viewDependencies.dependencies.settingsBounds, COMPONENT_MATRIX_2_AI_VIEW_STATE_KEYS),
            };
        case ViewIds.DEPENDENCY_MAP_2:
            return {
                dependencyMap: pick(viewDependencies.dependencies, AI_DEPENDENCY_MAP_STATE_KEYS),
                ...(groupLabels !== undefined ? { groupLabels } : {}),
            };
        case ViewIds.BUBBLE:
            return {
                bubbleChart: pick(viewDependencies.dependencies, BUBBLE_CHART_AI_VIEW_STATE_KEYS),
                ...(groupLabels !== undefined ? { groupLabels } : {}),
            };
        case ViewIds.TIMELINE:
            return {
                timeline: pick(viewDependencies.dependencies, TIMELINE_AI_VIEW_STATE_KEYS),
            };
    }
};
const getAiSettableViewSettings = (viewDependencies: AiViewDependencies): Record<string, unknown> => {
    switch (viewDependencies.viewId) {
        case ViewIds.COMPONENT_MATRIX_2:
            return pick(toAiComponentMatrix2ViewSettings(viewDependencies.dependencies.settableViewSettings), COMPONENT_MATRIX_2_AI_CONTEXT_SETTING_KEYS);
        case ViewIds.DEPENDENCY_MAP_2:
            return pick(viewDependencies.dependencies.settableViewSettings, DEPENDENCY_MAP_AI_CONTEXT_SETTING_KEYS);
        case ViewIds.BUBBLE: {
            const settings = pick(viewDependencies.dependencies.settableViewSettings, BUBBLE_AI_CONTEXT_SETTING_KEYS);
            return { ...settings, labelFilter: settings.labelFilter ?? null };
        }
        case ViewIds.TIMELINE:
            return pick(toAiTimelineViewSettings(viewDependencies.dependencies.viewSettings), TIMELINE_AI_CONTEXT_SETTING_KEYS);
    }
};
const translateLayoutOrientation = (settings: Record<string, unknown>): Record<string, unknown> => {
    const layoutOrientation = toLayoutOrientationAiValue(settings.layoutOrientation);
    if (layoutOrientation === undefined)
        return settings;
    return { ...settings, layoutOrientation };
};
type GetAiCurrentSettingsArgs = {
    currentView: ViewIds | null;
    settingsByViewId: ViewpointModeViewSettingsWithChanges['currentSettings'];
    viewDependencies: AiViewDependencies | undefined;
};
const getAiCurrentSettings = ({ currentView, settingsByViewId, viewDependencies, }: GetAiCurrentSettingsArgs): Record<string, unknown> => {
    if (viewDependencies) {
        return getAiSettableViewSettings(viewDependencies);
    }
    switch (currentView) {
        case ViewIds.MODERNIZED_BLOCK_DIAGRAM:
            return translateLayoutOrientation(pick(settingsByViewId[ViewIds.MODERNIZED_BLOCK_DIAGRAM], MODERNIZED_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS));
        case ViewIds.RELATIONSHIPS_3:
            return pick(settingsByViewId[ViewIds.RELATIONSHIPS_3], RELATIONSHIPS_AI_CONTEXT_SETTING_KEYS);
        case ViewIds.FLEXIBLE_BLOCK_DIAGRAM:
            return translateLayoutOrientation(pick(settingsByViewId[ViewIds.FLEXIBLE_BLOCK_DIAGRAM], FLEXIBLE_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS));
        case ViewIds.BLOCKS:
            return pick(settingsByViewId[ViewIds.BLOCKS], BLOCKS_AI_CONTEXT_SETTING_KEYS);
        default:
            return {};
    }
};
const toStoredModernizedBlockDiagramViewSettings = (settings: Record<string, unknown>): Record<string, unknown> => {
    if (settings.layoutOrientation === undefined) {
        return { ...settings };
    }
    const layoutOrientation = toLayoutOrientation(settings.layoutOrientation);
    if (layoutOrientation === null) {
        const { layoutOrientation: unrecognised, ...rest } = settings;
        logError(new Error('AI supplied an unrecognised layout orientation'), null, {
            tags: { team: 'insight' },
            code: 'ai_view_settings_unknown_layout_orientation',
            unrecognised,
        });
        return rest;
    }
    return { ...settings, layoutOrientation };
};
const getViewInstanceId = ({ viewProperties, viewPropertiesViewId }: CurrentViewPropertiesState, expectedViewId: ViewIds): string | undefined => {
    if (viewPropertiesViewId !== expectedViewId ||
        !hasViewInstanceId(viewProperties)) {
        return undefined;
    }
    return viewProperties.viewInstanceId;
};
const requireViewInstanceId = (currentViewProperties: CurrentViewPropertiesState, expectedViewId: ViewIds): string => {
    const viewInstanceId = getViewInstanceId(currentViewProperties, expectedViewId);
    if (viewInstanceId === undefined) {
        throw new Error(`The ${viewIdToUserFriendlyName[expectedViewId]} view is not ready to change its groups.`);
    }
    return viewInstanceId;
};
const getRequestedCollapsedGroupIds = ({ settings, }: ViewSettingsChangePayload): string[] | undefined => 'collapsedGroupIds' in settings ? settings.collapsedGroupIds : undefined;
const VIEW_IDS_ADDRESSED_BY_INSTANCE_ID: ReadonlySet<ViewIds> = new Set([
    ViewIds.MODERNIZED_BLOCK_DIAGRAM,
    ViewIds.RELATIONSHIPS_3,
]);
type GroupCollapseSources = {
    groupLabels: Record<string, string> | undefined;
    currentViewProperties: CurrentViewPropertiesState;
};
const findGroupCollapseRejection = (payload: ViewSettingsChangePayload, { groupLabels, currentViewProperties }: GroupCollapseSources): ValidationRejection | undefined => {
    const requestedIds = getRequestedCollapsedGroupIds(payload);
    if (requestedIds === undefined)
        return undefined;
    if (VIEW_IDS_ADDRESSED_BY_INSTANCE_ID.has(payload.type) &&
        getViewInstanceId(currentViewProperties, payload.type) === undefined) {
        return staticRejection('groupCollapseViewLoading', `${viewIdToUserFriendlyName[payload.type]} is still loading. Try again.`);
    }
    const knownIds = Object.keys(groupLabels ?? {});
    if (knownIds.length === 0) {
        return staticRejection('groupCollapseNoGroups', `${viewIdToUserFriendlyName[payload.type]} has no groups to collapse or expand.`);
    }
    const unknownIds = requestedIds.filter(id => id !== WILDCARD && !knownIds.includes(id));
    if (unknownIds.length === 0)
        return undefined;
    return {
        kind: 'groupCollapseUnknownIds',
        buildReason: showName => `Unknown group ids: ${unknownIds.map(showName).join(', ')}. Use ids from viewState.groupLabels.`,
    };
};
export type BuildChangeViewSettingsCommandsArgs = {
    currentView: ViewIds | null;
    settingsByViewId: ViewpointModeViewSettingsWithChanges['currentSettings'];
    currentViewProperties: CurrentViewPropertiesState;
};
export const buildChangeViewSettingsCommand = ({ currentView, settingsByViewId, currentViewProperties, }: BuildChangeViewSettingsCommandsArgs): AiControlledCommand<typeof viewSettingsCommandDefinition> => {
    const viewDependencies = readAiViewDependencies(currentView, currentViewProperties);
    const viewState = getAiViewRuntimeState(currentView, currentViewProperties, viewDependencies);
    return {
        ...viewSettingsCommandDefinition,
        context: {
            currentView,
            currentSettings: getAiCurrentSettings({
                currentView,
                settingsByViewId,
                viewDependencies,
            }),
            ...(viewState !== undefined ? { viewState } : {}),
        },
    };
};
const redirectToRenderedView = (payload: ViewSettingsChangePayload): ViewSettingsChangePayload => {
    if (payload.type !== ViewIds.MODERNIZED_BLOCK_DIAGRAM ||
        toRenderedViewpointViewId(payload.type) !== ViewIds.FLEXIBLE_BLOCK_DIAGRAM) {
        return payload;
    }
    return { ...payload, type: ViewIds.FLEXIBLE_BLOCK_DIAGRAM };
};
const normalizeRequestedSettings = (payload: ViewSettingsChangePayload, viewpointModeViewSettings: ViewpointModeViewSettingsShapeWithoutSymbols): ViewSettingsChangePayload => {
    const requestedKeys = Object.keys(payload.settings);
    if (payload.type === ViewIds.COMPONENT_MATRIX_2) {
        const stored = viewpointModeViewSettings[ViewIds.COMPONENT_MATRIX_2];
        return {
            ...payload,
            settings: pick(toAiComponentMatrix2ViewSettings({
                ...stored,
                ...toStoredComponentMatrix2ViewSettings(payload.settings),
            }), requestedKeys),
        };
    }
    if (payload.type === ViewIds.TIMELINE) {
        const stored = viewpointModeViewSettings[ViewIds.TIMELINE];
        return {
            ...payload,
            settings: pick(toAiTimelineViewSettings({
                ...stored,
                ...toStoredTimelineViewSettings(payload.settings, stored.persistedCollapsedState),
            }), requestedKeys),
        };
    }
    return payload;
};
const hasEqualSettings = (currentSettings: Record<string, unknown>, settings: Record<string, unknown>): boolean => Object.entries(settings).every(([key, value]) => isEqual(currentSettings[key], value));
const transformAiSettings = (payload: ViewSettingsChangePayload, currentViewProperties: CurrentViewPropertiesState): Record<string, unknown> => {
    if (payload.type === ViewIds.COMPONENT_MATRIX_2) {
        return toStoredComponentMatrix2ViewSettings(payload.settings);
    }
    if (payload.type === ViewIds.BUBBLE) {
        return toStoredBubbleChartViewSettings(payload.settings);
    }
    if (payload.type === ViewIds.TIMELINE &&
        isTimelineSettingsControlsDependencies(currentViewProperties.viewProperties)) {
        return toStoredTimelineViewSettings(payload.settings, currentViewProperties.viewProperties.viewSettings.persistedCollapsedState);
    }
    if (payload.type === ViewIds.MODERNIZED_BLOCK_DIAGRAM ||
        payload.type === ViewIds.FLEXIBLE_BLOCK_DIAGRAM) {
        return toStoredModernizedBlockDiagramViewSettings(payload.settings);
    }
    return { ...payload.settings };
};
type GroupCollapseActions = {
    expandAll: () => Action<unknown>;
    collapseAll: () => Action<unknown>;
    setCollapsedGroupIds: (collapsedGroupIds: string[]) => Action<unknown>;
};
const dispatchGroupCollapseChange = (collapsedGroupIds: string[], actions: GroupCollapseActions, viewInstanceId?: string): void => {
    const dispatch = (action: Action<unknown>): void => viewInstanceId === undefined
        ? dispatchAction(action)
        : dispatchAction(action, viewInstanceId);
    if (collapsedGroupIds.length === 0) {
        dispatch(actions.expandAll());
    }
    else if (isAllCollapsed(collapsedGroupIds)) {
        dispatch(actions.collapseAll());
    }
    else {
        dispatch(actions.setCollapsedGroupIds(collapsedGroupIds));
    }
};
export const executeChangeViewSettings = (payload: ViewSettingsChangePayload, { currentViewProperties }: CommandExecutionState): void => {
    const settings = transformAiSettings(payload, currentViewProperties);
    if (payload.type === ViewIds.MODERNIZED_BLOCK_DIAGRAM &&
        payload.settings.collapsedGroupIds !== undefined) {
        const viewInstanceId = requireViewInstanceId(currentViewProperties, ViewIds.MODERNIZED_BLOCK_DIAGRAM);
        dispatchGroupCollapseChange(payload.settings.collapsedGroupIds, {
            expandAll: () => relationshipDiagramExpandAllGroups({
                viewId: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
            }),
            collapseAll: () => relationshipDiagramCollapseAllGroups({
                viewId: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
            }),
            setCollapsedGroupIds: collapsedGroupIds => relationshipDiagramSetCollapsedGroupIds({
                viewId: ViewIds.MODERNIZED_BLOCK_DIAGRAM,
                collapsedGroupIds,
            }),
        }, viewInstanceId);
    }
    if (payload.type === ViewIds.RELATIONSHIPS_3 &&
        payload.settings.collapsedGroupIds !== undefined) {
        const viewInstanceId = requireViewInstanceId(currentViewProperties, ViewIds.RELATIONSHIPS_3);
        dispatchAction(relationshipsViewSetCollapsedGroupIds({
            collapsedGroupIds: payload.settings.collapsedGroupIds,
        }), viewInstanceId);
    }
    if (payload.type === ViewIds.FLEXIBLE_BLOCK_DIAGRAM &&
        payload.settings.collapsedGroupIds !== undefined) {
        dispatchGroupCollapseChange(payload.settings.collapsedGroupIds, {
            expandAll: flexibleBlockDiagramExpandAll,
            collapseAll: flexibleBlockDiagramCollapseAll,
            setCollapsedGroupIds: collapsedGroupIds => flexibleBlockDiagramSetCollapsedGroupIds({ collapsedGroupIds }),
        });
    }
    dispatchAction(updateViewSettings({
        viewId: payload.type,
        settings,
        persistent: true,
    }));
};
const AI_SETTING_KEYS_BY_VIEW_ID: Record<(typeof AI_VIEW_IDS)[number], readonly string[]> = {
    [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: MODERNIZED_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.COMPONENT_MATRIX_2]: COMPONENT_MATRIX_2_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.DEPENDENCY_MAP_2]: DEPENDENCY_MAP_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.RELATIONSHIPS_3]: RELATIONSHIPS_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.BUBBLE]: BUBBLE_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.TIMELINE]: TIMELINE_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: FLEXIBLE_BLOCK_DIAGRAM_AI_CONTEXT_SETTING_KEYS,
    [ViewIds.BLOCKS]: BLOCKS_AI_CONTEXT_SETTING_KEYS,
};
/**
 * The requested keys when none of them is a setting of the view, so the request
 * can only be a no-op. A request with at least one recognised key is left to
 * `isApplied`, because neither list is guaranteed to be complete.
 */
const findUnrecognisedSettingKeys = ({ type, settings }: ViewSettingsChangePayload, viewpointModeViewSettings: ViewpointModeViewSettingsShapeWithoutSymbols): string[] => {
    const requestedKeys = Object.keys(settings ?? {});
    const recognisedKeys = new Set([
        ...AI_SETTING_KEYS_BY_VIEW_ID[type],
        ...Object.keys(viewpointModeViewSettings[type] ?? {}),
    ]);
    return requestedKeys.some(key => recognisedKeys.has(key))
        ? []
        : requestedKeys;
};
const findViewSettingsRejection = (payload: ViewSettingsChangePayload, context: ChangeViewSettingsContext, state: CommandExecutionState): ValidationRejection | undefined => {
    const { switchableViewIds, viewpointModeViewSettings } = state;
    if (!switchableViewIds.includes(payload.type)) {
        return staticRejection('unavailableView', `The requested view is not available: ${payload.type}.`);
    }
    if (payload.type !== context.currentView) {
        return staticRejection('viewNotOpen', `${viewIdToUserFriendlyName[payload.type]} is not the open view. Ask to open supported view first`);
    }
    if (isEmpty(payload.settings)) {
        return staticRejection('emptySettings', 'Nothing to change: include the settings to change.');
    }
    const unrecognisedSettingKeys = findUnrecognisedSettingKeys(payload, viewpointModeViewSettings);
    if (unrecognisedSettingKeys.length > 0) {
        return {
            kind: 'unrecognisedSettings',
            buildReason: showName => `${viewIdToUserFriendlyName[payload.type]} does not support these settings: ${unrecognisedSettingKeys.map(showName).join(', ')}. Send only supported settings.`,
        };
    }
    return findGroupCollapseRejection(payload, {
        groupLabels: context.viewState?.groupLabels,
        currentViewProperties: state.currentViewProperties,
    });
};
export const viewSettingsCommandDefinition = defineAiControlledCommand<ViewSettingsChangePayload, ChangeViewSettingsContext>({
    name: AiControlledCommandId.CHANGE_VIEW_SETTINGS,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                ViewSettingsChangePayload: {
                    oneOf: [
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "bubble",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/PartialSettableBubbleChartViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "timeline",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/AiTimelineViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "componentMatrix2",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/PartialSettableComponentMatrix2ViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "dependencyMap",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/AiDependencyMapViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "relationships3",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/PartialSettableRelationshipsViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "blocks",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/PartialSettableBlocksViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "flexibleBlockDiagram",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/AiFlexibleBlockDiagramViewSettings",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "modernizedBlockDiagram",
                                    description: "The type of the view whose settings to update. Must be the open view."
                                },
                                settings: {
                                    $ref: "#/components/schemas/PartialOmitSettableModernizedBlockDiagramViewSettingslayoutOrientationlayoutOrientationtop-to-bottomleft-to-rightright-to-leftbottom-to-topundefined",
                                    description: "A partial patch containing only configuration explicitly requested by\nthe user.\n\nInclude only supported keys. Include a key only if its requested value\ndiffers from `currentSettings`."
                                }
                            },
                            required: [
                                "type",
                                "settings"
                            ]
                        }
                    ]
                },
                PartialSettableBubbleChartViewSettings: {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component and reference types in the view\n(with their colours) and any conditional formatting. Hidden by default; set\n`true` to show it."
                        },
                        selectedComponentTypes: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Component type ids to plot. `['*']` includes every type and is the default;\nemit it when the user asks for all types. Otherwise match what the user\nasks for against `name` in viewState.bubbleChart.componentTypesById and\nemit that entry's `ids` unchanged: one name can cover several ids, and only\ncomponents of the listed types are shown."
                        },
                        background: {
                            oneOf: [
                                {
                                    "const": "none"
                                },
                                {
                                    "const": "grid"
                                },
                                {
                                    "const": "time"
                                },
                                {
                                    "const": "custom"
                                }
                            ],
                            description: "The chart background. `none` and `grid` only control whether gridlines are\ndrawn behind the bubbles. `time` fixes both axes to a 1\u20135 range with\nquadrant lines at the midpoints and a label in each quadrant. `custom` draws\nthe quadrant lines from `customQuadrantSettings`."
                        },
                        customQuadrantSettings: {
                            $ref: "#/components/schemas/BubbleChartQuadrantSettings",
                            description: "Axis bounds and quadrant labels used when `background` is `custom`."
                        },
                        bubbleAreaScaleFactor: {
                            type: "number",
                            description: "Multiplier applied to every bubble's area, scaling all bubbles up or down\ntogether (labels scale with their bubble)."
                        },
                        showBubblesWithZeroValue: {
                            type: "boolean",
                            description: "Whether to plot bubbles whose size-field value is 0. Treated as `true` when\nunset."
                        },
                        selectedQuadrant: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    "const": "topLeft"
                                },
                                {
                                    "const": "topRight"
                                },
                                {
                                    "const": "bottomLeft"
                                },
                                {
                                    "const": "bottomRight"
                                }
                            ],
                            description: "The quadrant the chart is zoomed into, or `null`/absent for the whole chart."
                        },
                        selectedFieldNameX: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "The field on the component type whose value positions each bubble on the X\naxis, or `null` for none. Pick one from\nviewState.bubbleChart.componentAxisFieldNames. Include an axis field only\nwhen viewState.bubbleChart.canSetComponentAxisFields is true."
                        },
                        selectedFieldNameY: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "The field on the component type whose value positions each bubble on the Y\naxis, or `null` for none. Pick one from\nviewState.bubbleChart.componentAxisFieldNames. Include an axis field only\nwhen viewState.bubbleChart.canSetComponentAxisFields is true."
                        },
                        selectedFieldNameRadius: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "The field on the component type whose value determines bubble size, or\n`null` for none. Bubbles are scaled by area, not radius. Pick one from\nviewState.bubbleChart.componentAxisFieldNames. Include an axis field only\nwhen viewState.bubbleChart.canSetComponentAxisFields is true."
                        },
                        labelFilter: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    $ref: "#/components/schemas/OmitLabelFilterfieldNamefieldNamestring"
                                }
                            ],
                            description: "Restricts which bubbles are labelled to those within a value range on one\ndimension. `null` removes it. Absent keeps the current filter, except when\nthe same call sets any axis field \u2014 assigning an axis always clears it.\n\nInclude this only when viewState.bubbleChart.canSetLabelFilter is true.\nTake `dimension` and `fieldName` from the matching entry in\nviewState.bubbleChart.labelFilterTargets, and set `min` and `max` within\nthat entry's `range`. That list describes the axes as they are configured\nnow, so do not assign an axis field and filter on it in the same call:\nassign the field, then filter next."
                        }
                    },
                    required: [],
                    description: "Make all properties in T optional"
                },
                BubbleChartQuadrantSettings: {
                    type: "object",
                    properties: {
                        xMinimum: {
                            type: "number",
                            description: "X-axis minimum; the vertical quadrant divider is drawn at the midpoint between this and `xMaximum`."
                        },
                        xMaximum: {
                            type: "number",
                            description: "X-axis maximum."
                        },
                        yMinimum: {
                            type: "number",
                            description: "Y-axis minimum; the horizontal quadrant divider is drawn at the midpoint between this and `yMaximum`."
                        },
                        yMaximum: {
                            type: "number",
                            description: "Y-axis maximum."
                        },
                        labels: {
                            type: "array",
                            prefixItems: [
                                {
                                    type: "string"
                                },
                                {
                                    type: "string"
                                },
                                {
                                    type: "string"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            additionalItems: false,
                            description: "The four quadrant labels, in order: top-left, top-right, bottom-left, bottom-right."
                        }
                    },
                    required: [
                        "xMinimum",
                        "xMaximum",
                        "yMinimum",
                        "yMaximum",
                        "labels"
                    ]
                },
                OmitLabelFilterfieldNamefieldNamestring: {
                    type: "object",
                    properties: {
                        dimension: {
                            oneOf: [
                                {
                                    "const": "selectedFieldNameX"
                                },
                                {
                                    "const": "selectedFieldNameY"
                                },
                                {
                                    "const": "selectedFieldNameRadius"
                                }
                            ],
                            description: "Which plotted dimension the range applies to: the X, Y, or bubble-size field."
                        },
                        min: {
                            type: "number",
                            description: "Inclusive lower bound; only bubbles whose value is at least this are labelled."
                        },
                        max: {
                            type: "number",
                            description: "Inclusive upper bound; only bubbles whose value is at most this are labelled."
                        },
                        fieldName: {
                            type: "string"
                        }
                    },
                    required: [
                        "dimension",
                        "min",
                        "max",
                        "fieldName"
                    ]
                },
                AiTimelineViewSettings: {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component types in the view (with their\ncolours) and any conditional formatting. Hidden by default; set `true` to\nshow it."
                        },
                        domainRange: {
                            oneOf: [
                                {
                                    type: "array",
                                    prefixItems: [
                                        {
                                            oneOf: [
                                                {
                                                    type: "null"
                                                },
                                                {
                                                    type: "string"
                                                }
                                            ]
                                        },
                                        {
                                            oneOf: [
                                                {
                                                    type: "null"
                                                },
                                                {
                                                    type: "string"
                                                }
                                            ]
                                        }
                                    ],
                                    additionalItems: false
                                },
                                {
                                    type: "object",
                                    properties: {
                                        period: {
                                            oneOf: [
                                                {
                                                    "const": "this",
                                                    description: "The current unit relative to now, e.g. this month."
                                                },
                                                {
                                                    "const": "last",
                                                    description: "The whole unit before the current one, e.g. last month."
                                                },
                                                {
                                                    "const": "next",
                                                    description: "The whole unit after the current one, e.g. next month."
                                                }
                                            ],
                                            description: "A whole period relative to now."
                                        },
                                        unit: {
                                            $ref: "#/components/schemas/RelativeDateUnit",
                                            description: "The unit of time the period spans."
                                        }
                                    },
                                    required: [
                                        "period",
                                        "unit"
                                    ]
                                },
                                {
                                    type: "object",
                                    properties: {
                                        period: {
                                            oneOf: [
                                                {
                                                    "const": "inTheNext",
                                                    description: "A forward window spanning `amount` units, starting now."
                                                },
                                                {
                                                    "const": "inThePast",
                                                    description: "A backward window spanning `amount` units, ending now."
                                                }
                                            ],
                                            description: "The direction of the offset window relative to now."
                                        },
                                        amount: {
                                            type: "number",
                                            description: "How many `unit`s the window spans."
                                        },
                                        unit: {
                                            $ref: "#/components/schemas/RelativeDateUnit",
                                            description: "The unit of time each step of `amount` spans."
                                        }
                                    },
                                    required: [
                                        "period",
                                        "amount",
                                        "unit"
                                    ]
                                }
                            ],
                            description: "The visible date window: an absolute `[start, end]` tuple of ISO date\nstrings (`null` leaves that end unbounded) or a relative range. Auto-fitting\nthe whole window to the data is the default \u2014 only set this when the user\nasks for a specific range."
                        },
                        selectedMilestoneFields: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Which date fields are shown as milestones (single points) on the timeline,\nby field `_id`. Contains `'*'` to show every milestone-eligible field;\notherwise only the listed fields are shown. Defaults to `[]` (no milestones\nshown).\n\nTake each `_id` from `viewState.timeline.milestoneFields`, and include this\nonly when `viewState.timeline.canSetMilestoneFields` is true."
                        },
                        selectedPhaseFields: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Which time-span fields are shown as phases (bars) on the timeline. A phase\nis a pair of date fields, identified by the start and end field `_id`s\njoined by a comma (`\"<startFieldId>,<endFieldId>\"`). Contains `'*'` to show\nevery phase-eligible field; otherwise only the listed fields are shown.\nDefaults to `['*']` (all phases shown).\n\nCopy each `_id` from `viewState.timeline.phaseFields` rather than composing\nit, and include this only when `viewState.timeline.canSetPhaseFields` is\ntrue."
                        },
                        gridPrecision: {
                            oneOf: [
                                {
                                    "const": "*"
                                },
                                {
                                    "const": "days"
                                },
                                {
                                    "const": "weeks"
                                },
                                {
                                    "const": "months"
                                },
                                {
                                    "const": "quarters"
                                },
                                {
                                    "const": "years"
                                }
                            ],
                            description: "Time granularity of the grid. `'*'` auto-selects a precision from the\nvisible date range and is the default \u2014 only set a fixed unit when the user\nexplicitly asks for one, and only one listed in\n`viewState.timeline.availableGridPrecisions`."
                        },
                        persistedCollapsedState: {
                            $ref: "#/components/schemas/PartialPersistedCollapsedState",
                            description: "Which groups and component field data rows are collapsed. Include only the\naxis you are changing; the one you leave out keeps what it has."
                        }
                    },
                    required: []
                },
                RelativeDateUnit: {
                    oneOf: [
                        {
                            "const": "day"
                        },
                        {
                            "const": "week"
                        },
                        {
                            "const": "month"
                        },
                        {
                            "const": "quarter"
                        },
                        {
                            "const": "year"
                        }
                    ]
                },
                PartialPersistedCollapsedState: {
                    type: "object",
                    properties: {
                        collapsedGroupIds: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Ids of the collapsed groups (their children are hidden). Take each id from\n`viewState.timeline.collapsibleGroupTargets`, or use `['*']` on its own to\ncollapse every group. Include this only when\n`viewState.timeline.canSetCollapsedGroups` is true."
                        },
                        collapsedFieldDataIds: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Ids of the component rows whose field data is collapsed. Expanding a row\nspreads overlapping phases onto separate lines and shows every field label\nin the labels column; collapsing overlaps those phases on a single line and\nshows only the component label. Take each id from\n`viewState.timeline.collapsibleFieldDataTargets`, or use `['*']` on its own\nto collapse every row. Include this only when\n`viewState.timeline.canSetCollapsedFieldData` is true."
                        }
                    },
                    required: [],
                    description: "Make all properties in T optional"
                },
                PartialSettableComponentMatrix2ViewSettings: {
                    type: "object",
                    properties: {
                        componentTypes: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Component type names to plot in the cells. An empty array plots every\ncomponent type present in the data. Defaults to `[]` (all types). Take each\nname from `viewState.componentMatrix2.availableComponentTypes`."
                        },
                        xAxis: {
                            oneOf: [
                                {
                                    type: "object",
                                    properties: {
                                        kind: {
                                            "const": "none",
                                            description: "The axis is not grouped: a single row or column holds every component."
                                        }
                                    },
                                    required: [
                                        "kind"
                                    ]
                                },
                                {
                                    $ref: "#/components/schemas/kindfieldfieldNamestringComponentMatrix2AxisDisplaySettings"
                                },
                                {
                                    $ref: "#/components/schemas/kindreferencereferenceSelectedReferenceTypeInfoComponentMatrix2AxisDisplaySettings"
                                },
                                {
                                    $ref: "#/components/schemas/kindparentComponentMatrix2AxisDisplaySettings"
                                }
                            ],
                            description: "The x-axis (columns, horizontal). Defaults to `{ kind: 'none' }`\n(ungrouped). Include this only when\n`viewState.componentMatrix2.canSetAxes` is true."
                        },
                        yAxis: {
                            oneOf: [
                                {
                                    type: "object",
                                    properties: {
                                        kind: {
                                            "const": "none",
                                            description: "The axis is not grouped: a single row or column holds every component."
                                        }
                                    },
                                    required: [
                                        "kind"
                                    ]
                                },
                                {
                                    $ref: "#/components/schemas/kindfieldfieldNamestringComponentMatrix2AxisDisplaySettings"
                                },
                                {
                                    $ref: "#/components/schemas/kindreferencereferenceSelectedReferenceTypeInfoComponentMatrix2AxisDisplaySettings"
                                },
                                {
                                    $ref: "#/components/schemas/kindparentComponentMatrix2AxisDisplaySettings"
                                }
                            ],
                            description: "The y-axis (rows, vertical). Defaults to `{ kind: 'none' }` (ungrouped).\nInclude this only when `viewState.componentMatrix2.canSetAxes` is true."
                        },
                        showEmptyValues: {
                            type: "boolean",
                            description: "When `true`, each axis keeps an extra \"no value\" group \u2014 a column on the\nx-axis and a row on the y-axis \u2014 for the components that have no value for\nthe field (or no reference of the type) that axis groups by. When `false`,\nthose \"no value\" groups are hidden. Defaults to `false`."
                        },
                        colorHeadersByComponent: {
                            type: "boolean",
                            description: "When `true`, each row and column header is coloured by its component type's\ncolour; when `false`, all headers are grey. Defaults to `true`."
                        },
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown \u2014 a panel keying the component types in the\nview (with their colours) and any conditional formatting. Off by default.\nNamed as the shared legend infrastructure writes it."
                        }
                    },
                    required: [],
                    description: "Make all properties in T optional"
                },
                kindfieldfieldNamestringComponentMatrix2AxisDisplaySettings: {
                    type: "object",
                    properties: {
                        kind: {
                            "const": "field",
                            description: "Group this axis by the values of a list field."
                        },
                        fieldName: {
                            type: "string",
                            description: "Name of the list field whose values group this axis. Take it from\n`viewState.componentMatrix2.axisFieldNames`."
                        },
                        sortOrder: {
                            oneOf: [
                                {
                                    "const": "default"
                                },
                                {
                                    "const": "alpha-az"
                                },
                                {
                                    "const": "alpha-za"
                                },
                                {
                                    "const": "count-high-low"
                                },
                                {
                                    "const": "count-low-high"
                                }
                            ],
                            description: "How the groups on this axis are ordered. Omit to keep the current order."
                        },
                        displayText: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "A custom label for this axis's header. `null` uses the automatic label (the\nfield name, the reference name, or \"Hierarchy\" for a parent axis). Omit to\nkeep the current label."
                        },
                        additionalHeaderFieldNames: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Names of extra fields whose values are shown as badges inside this axis's\nheader cards. Take them from\n`viewState.componentMatrix2.axisHeaderFieldNamesX` for the x-axis and from\n`viewState.componentMatrix2.axisHeaderFieldNamesY` for the y-axis. Those\nlists describe the axes as they are configured now, so do not change this\naxis and set its badges in the same call: change the axis, set the badges\nnext. Omit to keep the current badges."
                        }
                    },
                    required: [
                        "kind",
                        "fieldName"
                    ]
                },
                kindreferencereferenceSelectedReferenceTypeInfoComponentMatrix2AxisDisplaySettings: {
                    type: "object",
                    properties: {
                        kind: {
                            "const": "reference",
                            description: "Group this axis by a component type reached from each plotted component\nby following a reference."
                        },
                        reference: {
                            $ref: "#/components/schemas/SelectedReferenceTypeInfo",
                            description: "The reference to follow. Must match a reference type that is currently\ntraversable from the data: take one entry from\n`viewState.componentMatrix2.axisReferences` and copy it as it is given."
                        },
                        sortOrder: {
                            oneOf: [
                                {
                                    "const": "default"
                                },
                                {
                                    "const": "alpha-az"
                                },
                                {
                                    "const": "alpha-za"
                                },
                                {
                                    "const": "count-high-low"
                                },
                                {
                                    "const": "count-low-high"
                                }
                            ],
                            description: "How the groups on this axis are ordered. Omit to keep the current order."
                        },
                        displayText: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "A custom label for this axis's header. `null` uses the automatic label (the\nfield name, the reference name, or \"Hierarchy\" for a parent axis). Omit to\nkeep the current label."
                        },
                        additionalHeaderFieldNames: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Names of extra fields whose values are shown as badges inside this axis's\nheader cards. Take them from\n`viewState.componentMatrix2.axisHeaderFieldNamesX` for the x-axis and from\n`viewState.componentMatrix2.axisHeaderFieldNamesY` for the y-axis. Those\nlists describe the axes as they are configured now, so do not change this\naxis and set its badges in the same call: change the axis, set the badges\nnext. Omit to keep the current badges."
                        }
                    },
                    required: [
                        "kind",
                        "reference"
                    ]
                },
                SelectedReferenceTypeInfo: {
                    type: "object",
                    properties: {
                        referenceWorkspaceId: {
                            type: "string"
                        },
                        referenceTypeId: {
                            type: "string"
                        },
                        linkedWorkspaceId: {
                            type: "string"
                        },
                        isOutgoing: {
                            type: "boolean",
                            description: "Traversal direction relative to the focal component: `true` follows outgoing\nreferences to their targets, `false` incoming references from their sources."
                        },
                        linkedComponentTypeName: {
                            type: "string",
                            description: "Optional narrowing to a specific linked-component type name. Used by\nCM2 (ARD-31884) to make the row/column dropdown distinguish between\ndifferent connected component types reachable via the same reference\ntype. Undefined \u21D2 match any linked component type (backward-compatible\ndefault for views that don't set it)."
                        }
                    },
                    required: [
                        "linkedWorkspaceId",
                        "isOutgoing"
                    ]
                },
                kindparentComponentMatrix2AxisDisplaySettings: {
                    type: "object",
                    properties: {
                        kind: {
                            "const": "parent",
                            description: "Group this axis by each component's parent in the component hierarchy."
                        },
                        sortOrder: {
                            oneOf: [
                                {
                                    "const": "default"
                                },
                                {
                                    "const": "alpha-az"
                                },
                                {
                                    "const": "alpha-za"
                                },
                                {
                                    "const": "count-high-low"
                                },
                                {
                                    "const": "count-low-high"
                                }
                            ],
                            description: "How the groups on this axis are ordered. Omit to keep the current order."
                        },
                        displayText: {
                            oneOf: [
                                {
                                    type: "null"
                                },
                                {
                                    type: "string"
                                }
                            ],
                            description: "A custom label for this axis's header. `null` uses the automatic label (the\nfield name, the reference name, or \"Hierarchy\" for a parent axis). Omit to\nkeep the current label."
                        },
                        additionalHeaderFieldNames: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Names of extra fields whose values are shown as badges inside this axis's\nheader cards. Take them from\n`viewState.componentMatrix2.axisHeaderFieldNamesX` for the x-axis and from\n`viewState.componentMatrix2.axisHeaderFieldNamesY` for the y-axis. Those\nlists describe the axes as they are configured now, so do not change this\naxis and set its badges in the same call: change the axis, set the badges\nnext. Omit to keep the current badges."
                        }
                    },
                    required: [
                        "kind"
                    ]
                },
                AiDependencyMapViewSettings: {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component and reference types in the view\n(with their colours) and any conditional formatting. Hidden by default; set\n`true` to show it."
                        },
                        showVertical: {
                            type: "boolean",
                            description: "How the map's root nodes are arranged. `true` lays them out as columns,\nwhich fits more root nodes on screen without scrolling; `false` lays them\nout as rows."
                        },
                        colorByReference: {
                            type: "boolean",
                            description: "When `true`, nodes are coloured by their connecting reference type instead\nof by their component type, ignoring any conditional formatting colours in\nfavour of the reference type colour."
                        },
                        degreesOfParenthood: {
                            type: "number",
                            description: "The number of reference-parent levels to display.\n\nInclude this only when\nviewState.dependencyMap.canSetParentDegrees is true.\n\nUse -1 when the user asks for all or unlimited reference-parent levels.\nOtherwise, use a whole number from 0 through\nviewState.dependencyMap.referenceParentMaxDepth.\n\nA value of 0 is valid and includes no reference-parent levels."
                        },
                        treeDepth: {
                            type: "number",
                            description: "The number of tree levels to keep expanded, counting each root as level 1.\n\nInclude this only when viewState.dependencyMap.canSetTreeDepth is true.\nUse a whole number from 1 through\nviewState.dependencyMap.maxTreeDepth.\n\nA value equal to maxTreeDepth means the tree is fully expanded."
                        }
                    },
                    required: []
                },
                PartialSettableRelationshipsViewSettings: {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component and reference types in the view\n(with their colours) and any conditional formatting. Hidden by default; set\n`true` to show it."
                        },
                        collapsedGroupIds: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Ids of the groups that are currently collapsed. A collapsed group hides its\nchildren and renders as a single node; expanding restores them. Ids listed\nhere are collapsed; all other groups are expanded. Two special values:\n`[]` expands all groups, and `['*']` collapses all groups. Only has an\neffect when grouping rules are provided; when there are no grouping rules\nprovided, this setting should be left unchanged."
                        },
                        bundleRelationships: {
                            type: "boolean",
                            description: "Whether relationships are bundled. When `true`, each edge is drawn as a\ncubic basis spline routed through the centres of the circles enclosing its\nendpoints in the circle-packing hierarchy (up to their lowest common\nancestor and back down), so edges sharing a path curve together and clutter\nis reduced. When `false`, every reference is drawn as its own direct line."
                        }
                    },
                    required: [],
                    description: "Make all properties in T optional"
                },
                PartialSettableBlocksViewSettings: {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component and reference types in the view\n(with their colours) and any conditional formatting. Hidden by default; set\n`true` to show it."
                        },
                        componentStyle: {
                            oneOf: [
                                {
                                    "const": "shape"
                                },
                                {
                                    "const": "icon"
                                }
                            ],
                            description: "How each component is drawn. `'shape'` renders it as its shape in a box;\n`'icon'` renders it as its icon in a box."
                        },
                        linksAggregation: {
                            oneOf: [
                                {
                                    "const": "hide"
                                },
                                {
                                    "const": "separate"
                                },
                                {
                                    "const": "byReferenceType"
                                }
                            ],
                            description: "How references between components are routed. `'separate'` (the default when\nabsent) draws every reference as its own line. `'byReferenceType'` bundles\nreferences of the same type that share a source or target component into one\ntrunk that branches out to each endpoint, reducing the number of parallel\nlines. `'hide'` draws no reference lines."
                        }
                    },
                    required: [],
                    description: "Make all properties in T optional"
                },
                AiFlexibleBlockDiagramViewSettings: {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component and reference types in the view\n(with their colours) and any conditional formatting. Hidden by default; set\n`true` to show it."
                        },
                        collapsedGroupIds: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Ids of the groups that are currently collapsed. A collapsed group hides its\nchildren and renders as a single node; expanding restores them. Ids listed\nhere are collapsed; all other groups are expanded. Two special values:\n`[]` expands all groups, and `['*']` collapses all groups. Only has an\neffect when grouping rules are provided; when there are no grouping rules\nprovided, this setting should be left unchanged."
                        },
                        separateReferences: {
                            type: "boolean",
                            description: "How references are routed. When `true`, every reference is drawn as its own\nline. When `false`, references of the same type that share a source or\ntarget component are bundled into one trunk that branches out to each other\nendpoint, reducing the number of parallel lines."
                        },
                        componentStyle: {
                            oneOf: [
                                {
                                    "const": "shape"
                                },
                                {
                                    "const": "icon"
                                }
                            ],
                            description: "How each component is drawn. `'shape'` renders it as its shape in a box;\n`'icon'` renders it as its icon in a box."
                        },
                        layoutOrientation: {
                            oneOf: [
                                {
                                    "const": "top-to-bottom"
                                },
                                {
                                    "const": "left-to-right"
                                },
                                {
                                    "const": "right-to-left"
                                },
                                {
                                    "const": "bottom-to-top"
                                }
                            ]
                        },
                        hideReferences: {
                            type: "boolean",
                            description: "Omit reference lines entirely. Takes precedence over `separateReferences`\nwhen `true`, so combining or separating references has no visible effect\nuntil this is `false`. When `currentSettings.hideReferences` is `true`\nand the user asks to combine or separate references, include this key\nset to `false` alongside `separateReferences`."
                        },
                        layoutType: {
                            oneOf: [
                                {
                                    "const": "process"
                                },
                                {
                                    "const": "compact"
                                },
                                {
                                    "const": "seriesparallel"
                                },
                                {
                                    "const": "grid"
                                }
                            ],
                            description: "How blocks are arranged:\n- `process`: a linear flow in the direction of `layoutOrientation`, best\n  for sequential steps.\n- `compact`: minimizes the space the diagram takes up.\n- `seriesparallel`: groups parallel branches together, best for flows\n  that branch and rejoin.\n- `grid`: aligns blocks into even rows and columns."
                        }
                    },
                    required: []
                },
                "PartialOmitSettableModernizedBlockDiagramViewSettingslayoutOrientationlayoutOrientationtop-to-bottomleft-to-rightright-to-leftbottom-to-topundefined": {
                    type: "object",
                    properties: {
                        isLegendActive: {
                            type: "boolean",
                            description: "Whether the legend is shown. The legend is a panel that acts as a key to\nthe visualization, listing the component and reference types in the view\n(with their colours) and any conditional formatting. Hidden by default; set\n`true` to show it."
                        },
                        collapsedGroupIds: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Ids of the groups that are currently collapsed. A collapsed group hides its\nchildren and renders as a single node; expanding restores them. Ids listed\nhere are collapsed; all other groups are expanded. Two special values:\n`[]` expands all groups, and `['*']` collapses all groups. Only has an\neffect when grouping rules are provided; when there are no grouping rules\nprovided, this setting should be left unchanged."
                        },
                        separateReferences: {
                            type: "boolean",
                            description: "How references are routed. When `true`, every reference is drawn as its own\nline. When `false`, references of the same type that share a source or\ntarget component are bundled into one trunk that branches out to each other\nendpoint, reducing the number of parallel lines."
                        },
                        componentStyle: {
                            oneOf: [
                                {
                                    "const": "shape"
                                },
                                {
                                    "const": "icon"
                                }
                            ],
                            description: "How each component is drawn. `'shape'` renders it as its shape in a box;\n`'icon'` renders it as its icon in a box."
                        },
                        layoutOrientation: {
                            oneOf: [
                                {
                                    "const": "top-to-bottom"
                                },
                                {
                                    "const": "left-to-right"
                                },
                                {
                                    "const": "right-to-left"
                                },
                                {
                                    "const": "bottom-to-top"
                                }
                            ]
                        }
                    },
                    required: []
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ViewSettingsChangePayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: CHANGE_VIEW_SETTINGS_CONTEXT_DESCRIPTION,
    isContext: isChangeViewSettingsContext,
    validate: (rawPayload, context, state) => {
        if (!(() => { const _io0 = (input: any): boolean => "bubble" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io1(input.settings)); const _io1 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (undefined === input.selectedComponentTypes || Array.isArray(input.selectedComponentTypes) && input.selectedComponentTypes.every((elem: any) => "string" === typeof elem)) && (undefined === input.background || "none" === input.background || "grid" === input.background || "time" === input.background || "custom" === input.background) && (undefined === input.customQuadrantSettings || "object" === typeof input.customQuadrantSettings && null !== input.customQuadrantSettings && _io2(input.customQuadrantSettings)) && (undefined === input.bubbleAreaScaleFactor || "number" === typeof input.bubbleAreaScaleFactor) && (undefined === input.showBubblesWithZeroValue || "boolean" === typeof input.showBubblesWithZeroValue) && (null === input.selectedQuadrant || undefined === input.selectedQuadrant || "topLeft" === input.selectedQuadrant || "topRight" === input.selectedQuadrant || "bottomLeft" === input.selectedQuadrant || "bottomRight" === input.selectedQuadrant) && (null === input.selectedFieldNameX || undefined === input.selectedFieldNameX || "string" === typeof input.selectedFieldNameX) && (null === input.selectedFieldNameY || undefined === input.selectedFieldNameY || "string" === typeof input.selectedFieldNameY) && (null === input.selectedFieldNameRadius || undefined === input.selectedFieldNameRadius || "string" === typeof input.selectedFieldNameRadius) && (null === input.labelFilter || undefined === input.labelFilter || "object" === typeof input.labelFilter && null !== input.labelFilter && _io3(input.labelFilter)); const _io2 = (input: any): boolean => "number" === typeof input.xMinimum && "number" === typeof input.xMaximum && "number" === typeof input.yMinimum && "number" === typeof input.yMaximum && (Array.isArray(input.labels) && (input.labels.length === 4 && "string" === typeof input.labels[0] && "string" === typeof input.labels[1] && "string" === typeof input.labels[2] && "string" === typeof input.labels[3])); const _io3 = (input: any): boolean => ("selectedFieldNameX" === input.dimension || "selectedFieldNameY" === input.dimension || "selectedFieldNameRadius" === input.dimension) && "number" === typeof input.min && "number" === typeof input.max && "string" === typeof input.fieldName; const _io4 = (input: any): boolean => "timeline" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io5(input.settings)); const _io5 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (null !== input.domainRange && (undefined === input.domainRange || (Array.isArray(input.domainRange) && (input.domainRange.length === 2 && (null === input.domainRange[0] || "string" === typeof input.domainRange[0]) && (null === input.domainRange[1] || "string" === typeof input.domainRange[1])) || "object" === typeof input.domainRange && null !== input.domainRange && _iu0(input.domainRange)))) && (undefined === input.selectedMilestoneFields || Array.isArray(input.selectedMilestoneFields) && input.selectedMilestoneFields.every((elem: any) => "string" === typeof elem)) && (undefined === input.selectedPhaseFields || Array.isArray(input.selectedPhaseFields) && input.selectedPhaseFields.every((elem: any) => "string" === typeof elem)) && (undefined === input.gridPrecision || "*" === input.gridPrecision || "days" === input.gridPrecision || "weeks" === input.gridPrecision || "months" === input.gridPrecision || "quarters" === input.gridPrecision || "years" === input.gridPrecision) && (undefined === input.persistedCollapsedState || "object" === typeof input.persistedCollapsedState && null !== input.persistedCollapsedState && false === Array.isArray(input.persistedCollapsedState) && _io8(input.persistedCollapsedState)); const _io6 = (input: any): boolean => ("this" === input.period || "last" === input.period || "next" === input.period) && ("day" === input.unit || "week" === input.unit || "month" === input.unit || "quarter" === input.unit || "year" === input.unit); const _io7 = (input: any): boolean => ("inTheNext" === input.period || "inThePast" === input.period) && "number" === typeof input.amount && ("day" === input.unit || "week" === input.unit || "month" === input.unit || "quarter" === input.unit || "year" === input.unit); const _io8 = (input: any): boolean => (undefined === input.collapsedGroupIds || Array.isArray(input.collapsedGroupIds) && input.collapsedGroupIds.every((elem: any) => "string" === typeof elem)) && (undefined === input.collapsedFieldDataIds || Array.isArray(input.collapsedFieldDataIds) && input.collapsedFieldDataIds.every((elem: any) => "string" === typeof elem)); const _io9 = (input: any): boolean => "componentMatrix2" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io10(input.settings)); const _io10 = (input: any): boolean => (undefined === input.componentTypes || Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "string" === typeof elem)) && (undefined === input.xAxis || "object" === typeof input.xAxis && null !== input.xAxis && _iu1(input.xAxis)) && (undefined === input.yAxis || "object" === typeof input.yAxis && null !== input.yAxis && _iu1(input.yAxis)) && (undefined === input.showEmptyValues || "boolean" === typeof input.showEmptyValues) && (undefined === input.colorHeadersByComponent || "boolean" === typeof input.colorHeadersByComponent) && (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive); const _io11 = (input: any): boolean => "none" === input.kind; const _io12 = (input: any): boolean => "field" === input.kind && "string" === typeof input.fieldName && (undefined === input.sortOrder || "default" === input.sortOrder || "alpha-az" === input.sortOrder || "alpha-za" === input.sortOrder || "count-high-low" === input.sortOrder || "count-low-high" === input.sortOrder) && (null === input.displayText || undefined === input.displayText || "string" === typeof input.displayText) && (undefined === input.additionalHeaderFieldNames || Array.isArray(input.additionalHeaderFieldNames) && input.additionalHeaderFieldNames.every((elem: any) => "string" === typeof elem)); const _io13 = (input: any): boolean => "reference" === input.kind && ("object" === typeof input.reference && null !== input.reference && _io14(input.reference)) && (undefined === input.sortOrder || "default" === input.sortOrder || "alpha-az" === input.sortOrder || "alpha-za" === input.sortOrder || "count-high-low" === input.sortOrder || "count-low-high" === input.sortOrder) && (null === input.displayText || undefined === input.displayText || "string" === typeof input.displayText) && (undefined === input.additionalHeaderFieldNames || Array.isArray(input.additionalHeaderFieldNames) && input.additionalHeaderFieldNames.every((elem: any) => "string" === typeof elem)); const _io14 = (input: any): boolean => (undefined === input.referenceWorkspaceId || "string" === typeof input.referenceWorkspaceId) && (undefined === input.referenceTypeId || "string" === typeof input.referenceTypeId) && "string" === typeof input.linkedWorkspaceId && "boolean" === typeof input.isOutgoing && (undefined === input.linkedComponentTypeName || "string" === typeof input.linkedComponentTypeName); const _io15 = (input: any): boolean => "parent" === input.kind && (undefined === input.sortOrder || "default" === input.sortOrder || "alpha-az" === input.sortOrder || "alpha-za" === input.sortOrder || "count-high-low" === input.sortOrder || "count-low-high" === input.sortOrder) && (null === input.displayText || undefined === input.displayText || "string" === typeof input.displayText) && (undefined === input.additionalHeaderFieldNames || Array.isArray(input.additionalHeaderFieldNames) && input.additionalHeaderFieldNames.every((elem: any) => "string" === typeof elem)); const _io16 = (input: any): boolean => "dependencyMap" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io17(input.settings)); const _io17 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (undefined === input.showVertical || "boolean" === typeof input.showVertical) && (undefined === input.colorByReference || "boolean" === typeof input.colorByReference) && (undefined === input.degreesOfParenthood || "number" === typeof input.degreesOfParenthood) && (undefined === input.treeDepth || "number" === typeof input.treeDepth); const _io18 = (input: any): boolean => "relationships3" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io19(input.settings)); const _io19 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (undefined === input.collapsedGroupIds || Array.isArray(input.collapsedGroupIds) && input.collapsedGroupIds.every((elem: any) => "string" === typeof elem)) && (undefined === input.bundleRelationships || "boolean" === typeof input.bundleRelationships); const _io20 = (input: any): boolean => "blocks" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io21(input.settings)); const _io21 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (undefined === input.componentStyle || "shape" === input.componentStyle || "icon" === input.componentStyle) && (undefined === input.linksAggregation || "hide" === input.linksAggregation || "separate" === input.linksAggregation || "byReferenceType" === input.linksAggregation); const _io22 = (input: any): boolean => "flexibleBlockDiagram" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io23(input.settings)); const _io23 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (undefined === input.collapsedGroupIds || Array.isArray(input.collapsedGroupIds) && input.collapsedGroupIds.every((elem: any) => "string" === typeof elem)) && (undefined === input.separateReferences || "boolean" === typeof input.separateReferences) && (undefined === input.componentStyle || "shape" === input.componentStyle || "icon" === input.componentStyle) && (undefined === input.layoutOrientation || "top-to-bottom" === input.layoutOrientation || "left-to-right" === input.layoutOrientation || "right-to-left" === input.layoutOrientation || "bottom-to-top" === input.layoutOrientation) && (undefined === input.hideReferences || "boolean" === typeof input.hideReferences) && (undefined === input.layoutType || "process" === input.layoutType || "compact" === input.layoutType || "seriesparallel" === input.layoutType || "grid" === input.layoutType); const _io24 = (input: any): boolean => "modernizedBlockDiagram" === input.type && ("object" === typeof input.settings && null !== input.settings && false === Array.isArray(input.settings) && _io25(input.settings)); const _io25 = (input: any): boolean => (undefined === input.isLegendActive || "boolean" === typeof input.isLegendActive) && (undefined === input.collapsedGroupIds || Array.isArray(input.collapsedGroupIds) && input.collapsedGroupIds.every((elem: any) => "string" === typeof elem)) && (undefined === input.separateReferences || "boolean" === typeof input.separateReferences) && (undefined === input.componentStyle || "shape" === input.componentStyle || "icon" === input.componentStyle) && (undefined === input.layoutOrientation || "top-to-bottom" === input.layoutOrientation || "left-to-right" === input.layoutOrientation || "right-to-left" === input.layoutOrientation || "bottom-to-top" === input.layoutOrientation); const _iu0 = (input: any): any => (() => {
            if ("this" === input.period || "last" === input.period || "next" === input.period)
                return _io6(input);
            else if ("inTheNext" === input.period || "inThePast" === input.period)
                return _io7(input);
            else
                return false;
        })(); const _iu1 = (input: any): any => (() => {
            if ("none" === input.kind)
                return _io11(input);
            else if ("field" === input.kind)
                return _io12(input);
            else if ("reference" === input.kind)
                return _io13(input);
            else if ("parent" === input.kind)
                return _io15(input);
            else
                return false;
        })(); const _iu2 = (input: any): any => (() => {
            if ("modernizedBlockDiagram" === input.type)
                return _io24(input);
            else if ("flexibleBlockDiagram" === input.type)
                return _io22(input);
            else if ("blocks" === input.type)
                return _io20(input);
            else if ("relationships3" === input.type)
                return _io18(input);
            else if ("dependencyMap" === input.type)
                return _io16(input);
            else if ("componentMatrix2" === input.type)
                return _io9(input);
            else if ("timeline" === input.type)
                return _io4(input);
            else if ("bubble" === input.type)
                return _io0(input);
            else
                return false;
        })(); return (input: any): input is ViewSettingsChangePayload => "object" === typeof input && null !== input && _iu2(input); })()(rawPayload)) {
            return {
                status: 'rejected',
                reason: 'The AI returned invalid view settings.',
            };
        }
        const payload = redirectToRenderedView(rawPayload);
        const rejection = findViewSettingsRejection(payload, context, state);
        if (rejection) {
            return toCommandRejection(rejection);
        }
        return {
            status: 'valid',
            payload: normalizeRequestedSettings(payload, state.viewpointModeViewSettings),
        };
    },
    execute: executeChangeViewSettings,
    isApplied: ({ type, settings }, { currentView, currentSettings }) => currentView === type && hasEqualSettings(currentSettings, settings),
});
